// Shared helpers for verdict panel (TO RESOLVE + BLOCKING)
// Keeps RiskAssessmentReport and LinkedReportOverview identical
export const HARD_FAIL_IDS = {
  KYC: "KYCHardFail",
  EFFORT: "EffortRateHardFail",
  DEFAULTER: "defaulterHardFail",
  RISK: "riskHardFail",
  GUARANTOR_SPAIN: "GUARANTOR_OUTSIDE_SPAIN",
  UNKNOWN: "UNKNOWN_HARD_FAIL",
};

export const getHardFailDisplay = (key, label, _t) => {
  const k = `${key} ${label}`.toLowerCase();
  if (k.includes("threshold") || k.includes("score below"))
    return {
      title: _t("scoreBelowThresholdTitle", "Score below approval threshold"),
      desc: _t(
        "scoreBelowThresholdDesc",
        "Overall application score is below the minimum required for approval."
      ),
    };
  if (k.includes("kyc") || k.includes("identification"))
    return {
      title: _t("identificationPolicyNotMet", "Identification policy not met"),
      desc: _t(
        "identificationPolicyDesc",
        "One or more applicants do not meet our standard identification and verification requirements."
      ),
    };
  if (k.includes("effort"))
    return {
      title: _t("effortRateExceedsThreshold", "Effort rate exceeds threshold"),
      desc: _t(
        "effortRateExceedsDesc",
        "Rent-to-income effort exceeds the permitted threshold."
      ),
    };
  if (k.includes("defaulter") || k.includes("default"))
    return {
      title: _t("paymentDefaultsDetected", "Payment defaults detected"),
      desc: _t(
        "paymentDefaultsDesc",
        "One or more applicants have unpaid payment defaults."
      ),
    };
  if (k.includes("risk"))
    return {
      title: _t("riskMitigationNotMet", "Risk mitigation not satisfied"),
      desc: _t(
        "riskMitigationDesc",
        "Risk mitigation requirements are not met."
      ),
    };
  if (k.includes("guarantor") && k.includes("spain"))
    return {
      title: _t("guarantorOutsideSpain", "Guarantor outside Spain"),
      desc: _t(
        "guarantorOutsideSpainDesc",
        "Guarantor residence outside Spain does not meet policy."
      ),
    };
  if (k.includes("unknown_hard_fail"))
    return {
      title: _t("blockingPolicyNotMet", "Blocking policy requirement not met"),
      desc: _t(
        "blockingPolicyNotMetDesc",
        "One or more applicants do not meet a mandatory policy requirement."
      ),
    };
  return { title: label || key, desc: "" };
};

export const buildHardFailWarnings = (applicants = []) => {
  const tenant = [];
  const guarantor = [];
  const safeApplicants = Array.isArray(applicants) ? applicants : [];

  safeApplicants.forEach((a) => {
    const reasons = [];
    const vals = a?.vals && typeof a.vals === "object" ? a.vals : {};
    const errorEntries = Array.isArray(a?.errorEntries)
      ? a.errorEntries.filter(
          (entry) => Array.isArray(entry) && entry.length >= 2
        )
      : [];
    const errMap = new Map(errorEntries.map(([k, v]) => [k, v]));
    if (
      vals.kycAmlFailCheck?.isHardFail ||
      errMap.get("kycAmlFailCheck")?.isHardFail
    )
      reasons.push(HARD_FAIL_IDS.KYC);
    if (
      vals.EFFORT_RATE_SCORE?.isHardFail ||
      [...errMap.values()].some((v) => v?.label === "EffortRateHardFail") ||
      errMap.get("EFFORT_RATE_SCORE")?.isHardFail
    ) {
      if (!reasons.includes(HARD_FAIL_IDS.EFFORT))
        reasons.push(HARD_FAIL_IDS.EFFORT);
    }
    if (
      vals.DEFAULT_HISTORY?.isHardFail ||
      [...errMap.values()].some((v) => v?.label === "defaulterHardFail")
    ) {
      if (!reasons.includes(HARD_FAIL_IDS.DEFAULTER))
        reasons.push(HARD_FAIL_IDS.DEFAULTER);
    }
    // riskHardFail intentionally hidden from UI
    if (
      errMap.get("GUARANTOR_OUTSIDE_SPAIN")?.isHardFail ||
      [...errMap.values()].some(
        (v) => v?.label === "guarantorLivingOutsideSpain"
      )
    ) {
      if (!reasons.includes(HARD_FAIL_IDS.GUARANTOR_SPAIN))
        reasons.push(HARD_FAIL_IDS.GUARANTOR_SPAIN);
    }
    Object.entries(vals).forEach(([, v]) => {
      if (
        v?.isHardFail &&
        v?.label &&
        !reasons.includes(v.label) &&
        !Object.values(HARD_FAIL_IDS).includes(v.label)
      )
        reasons.push(v.label);
    });
    errMap.forEach((v) => {
      if (
        v?.isHardFail &&
        v?.label &&
        !reasons.includes(v.label) &&
        !Object.values(HARD_FAIL_IDS).includes(v.label)
      )
        reasons.push(v.label);
    });
    if (!reasons.length && a?.isHardFail) reasons.push(HARD_FAIL_IDS.UNKNOWN);
    if (reasons.length) {
      const item = { id: a._id, label: a.name || a.label || a._id, reasons };
      if (a.role === "guarantor") guarantor.push(item);
      else tenant.push(item);
    }
  });
  return { tenant, guarantor, all: [...tenant, ...guarantor] };
};

export const buildBlockingRows = (
  hardFailWarnings,
  _t,
  isRejected,
  reportRecs = []
) => {
  const rows = [];
  const rowsByTitle = new Map();
  const warnings = Array.isArray(hardFailWarnings?.all)
    ? hardFailWarnings.all
    : [];
  const recommendations = Array.isArray(reportRecs)
    ? reportRecs
    : typeof reportRecs === "string"
      ? [reportRecs]
      : [];

  warnings.forEach((group) => {
    const reasons = Array.isArray(group?.reasons) ? group.reasons : [];
    reasons.forEach((reason) => {
      if (reason === HARD_FAIL_IDS.RISK) return;
      const disp = getHardFailDisplay(reason, reason, _t);
      const existing = rowsByTitle.get(disp.title);
      if (existing) {
        if (group.label && !existing.applicants.includes(group.label)) {
          existing.applicants.push(group.label);
        }
        return;
      }
      rowsByTitle.set(disp.title, {
        key: reason || disp.title,
        title: disp.title,
        desc: disp.desc,
        raw: reason,
        applicants: group.label ? [group.label] : [],
      });
    });
  });
  rows.push(
    ...Array.from(rowsByTitle.values()).map((row) => ({
      ...row,
      desc: row.applicants.length
        ? `${row.desc || ""}${row.desc ? " " : ""}${_t(
            "affectedApplicants",
            "Affected applicants"
          )}: ${row.applicants.join(", ")}`
        : row.desc,
    }))
  );
  if (rows.length === 0 && isRejected) {
    const thresholdRaw =
      recommendations.find(
        (recommendation) =>
          typeof recommendation === "string" &&
          /threshold|score below/i.test(recommendation)
      ) || "scoreBelowThreshold";
    const disp = getHardFailDisplay("scoreBelowThreshold", thresholdRaw, _t);
    rows.push({
      key: "threshold",
      title: disp.title,
      desc: thresholdRaw !== "scoreBelowThreshold" ? thresholdRaw : disp.desc,
      raw: thresholdRaw,
    });
  }
  return rows;
};
